import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import { fetchAptRequest, fetchAptRequests } from 'api/apartmentProcurement'

import { money } from './constants'
import { RoleContext } from './roleContext'
import RequestDetail from './RequestDetail'

// The two approval queues plus the decision history. The backend scopes the
// list to the caller (a cluster manager only sees their cluster), so this just
// splits by stage:
//   submitted        -> cluster managers act
//   cluster_approved -> the At Apartment head acts
//   decided          -> past approvals + rejections (read-only history)
const DECIDED_STATUSES = 'final_approved,in_daily,handed_over,closed,rejected'
const useStyles = makeStyles((theme) => ({
  section: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  heading: { fontWeight: 700, marginBottom: theme.spacing(1) },
  lede: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2), maxWidth: '74ch' }
}))

// Outcome of a decided request: who cleared/rejected it and when.
const decision = (r) => {
  if (r.status === 'rejected') {
    return {
      label: 'Rejected',
      color: '#c62828',
      by: r.rejection_stage ? `at ${String(r.rejection_stage).replace(/_/g, ' ')}` : '',
      when: r.rejected_at,
      note: r.rejection_reason || ''
    }
  }
  return {
    label: 'Approved',
    color: '#2e7d32',
    by: r.final_approved_by_name || r.cluster_approved_by_name || '',
    when: r.final_approved_at || r.cluster_approved_at,
    note: ''
  }
}

const Queue = ({ title, hint, rows, onOpen }) => {
  const classes = useStyles()
  return (
    <Paper className={classes.section} variant="outlined">
      <Typography className={classes.heading}>{title}</Typography>
      {!rows.length ? (
        <Typography color="textSecondary" variant="body2">
          {hint}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Request</TableCell>
              <TableCell>Apartment</TableCell>
              <TableCell>Cluster</TableCell>
              <TableCell align="right">Lines</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell>Raised by</TableCell>
              <TableCell>Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.uuid}
                hover
                style={{ cursor: 'pointer' }}
                onClick={() => onOpen(r.uuid)}>
                <TableCell>{r.title || r.kit_name || '(untitled)'}</TableCell>
                <TableCell>{r.apartment_name}</TableCell>
                <TableCell>{r.cluster_name}</TableCell>
                <TableCell align="right">{r.line_count}</TableCell>
                <TableCell align="right">{money(r.total_value)}</TableCell>
                <TableCell>{r.requested_by_name}</TableCell>
                <TableCell>
                  {r.submitted_at ? String(r.submitted_at).slice(0, 10) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  )
}

const DecidedHistory = ({ rows, onOpen }) => {
  const classes = useStyles()
  return (
    <Paper className={classes.section} variant="outlined">
      <Typography className={classes.heading}>Decided</Typography>
      {!rows.length ? (
        <Typography color="textSecondary" variant="body2">
          No approvals or rejections yet — cleared and rejected requests show here.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Request</TableCell>
              <TableCell>Apartment</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell>By</TableCell>
              <TableCell>When</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const d = decision(r)
              return (
                <TableRow
                  key={r.uuid}
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => onOpen(r.uuid)}>
                  <TableCell>{r.title || r.kit_name || '(untitled)'}</TableCell>
                  <TableCell>{r.apartment_name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={d.label}
                      style={{ color: '#fff', backgroundColor: d.color }}
                    />
                  </TableCell>
                  <TableCell>{d.by || '—'}</TableCell>
                  <TableCell>{d.when ? String(d.when).slice(0, 10) : '—'}</TableCell>
                  <TableCell align="right">{money(r.total_value)}</TableCell>
                  <TableCell>{d.note || '—'}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  )
}

const ApprovalsTab = () => {
  const role = React.useContext(RoleContext)
  const classes = useStyles()
  const [rows, setRows] = React.useState(null)
  const [decided, setDecided] = React.useState([])
  const [detail, setDetail] = React.useState(null)

  const load = React.useCallback(() => {
    fetchAptRequests({ status: 'submitted,cluster_approved' }).then(setRows)
    fetchAptRequests({ status: DECIDED_STATUSES })
      .then((r) => {
        const sorted = (r || []).slice().sort((a, b) => {
          const wa = decision(a).when || ''
          const wb = decision(b).when || ''
          return String(wb).localeCompare(String(wa))
        })
        setDecided(sorted)
      })
      .catch(() => setDecided([]))
  }, [])

  React.useEffect(load, [load])

  const openDetail = async (uuid) => setDetail(await fetchAptRequest(uuid))
  const refresh = async () => {
    load()
    if (detail) setDetail(await fetchAptRequest(detail.uuid))
  }

  if (!rows) return <LinearProgress />

  const clusterQueue = rows.filter((r) => r.status === 'submitted')
  const finalQueue = rows.filter((r) => r.status === 'cluster_approved')

  return (
    <Box>
      <Typography variant="body2" className={classes.lede}>
        Requests waiting for your approval, and the history of what's already been
        approved or rejected. Click any row to open the request.
      </Typography>
      <Queue
        title="Awaiting cluster approval"
        hint="Nothing waiting — new submissions land here."
        rows={clusterQueue}
        onOpen={openDetail}
      />
      {(role.is_central || finalQueue.length > 0) && (
        <Queue
          title="Awaiting final approval (At Apartment head)"
          hint="Nothing cleared by cluster managers yet."
          rows={finalQueue}
          onOpen={openDetail}
        />
      )}
      <DecidedHistory rows={decided} onOpen={openDetail} />
      {detail && (
        <RequestDetail
          request={detail}
          onClose={() => setDetail(null)}
          onChanged={refresh}
        />
      )}
    </Box>
  )
}

export default ApprovalsTab
